import { useQueryClient } from '@tanstack/react-query'
import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react'
import type { GeoFix, GeolocationPort } from '@/platform/geolocation'
import { openLocalStore, requestPersistentStorage } from '@/platform/localStore'
import type { MissionRepository } from './MissionRepository'
import { JournalSync } from './detection/JournalSync'
import { OperatorContext, type OperatorServices } from './OperatorServices'
import { PresenceBeat, presencePosition, type PresenceState } from './presence/PresenceBeat'
import { PositionSync, type SyncResult } from './tracking/PositionSync'

/**
 * Services du compte connecté : sa propre base locale (jamais celle d'un autre compte sur une tablette partagée),
 * l'envoi des positions en attente — y compris celles d'une session précédente de ce même compte — et la présence
 * (battement de 30 s, avec ou sans mission).
 */
export function OperatorProvider({
  userId,
  repository,
  geolocation,
  children,
}: {
  userId: string
  repository: MissionRepository
  geolocation: GeolocationPort
  children: ReactNode
}) {
  const store = useMemo(() => openLocalStore(userId), [userId])
  const sync = useMemo(() => new PositionSync(store, repository), [store, repository])
  const journalSync = useMemo(() => new JournalSync(store, repository), [store, repository])
  const queryClient = useQueryClient()
  const flushJournal = useCallback(async () => {
    const result = await journalSync.flush()
    // Une résidence fermée par le moteur (AUTO) quitte la liste « À faire » : relire les missions du compte.
    if (result.completed > 0) await queryClient.invalidateQueries({ queryKey: ['mission', userId] })
    return result
  }, [journalSync, queryClient, userId])
  const [syncState, setSyncState] = useState<SyncResult | null>(null)
  // Dernière mesure du port GPS : lue au moment du battement, jamais pendant le rendu.
  const [latestFix] = useState<{ fix: GeoFix | null }>(() => ({ fix: null }))
  const presenceBeat = useMemo(() => new PresenceBeat(repository, () => latestFix.fix), [repository, latestFix])
  const [presence, setPresence] = useState<PresenceState>(presenceBeat.current)

  const value = useMemo<OperatorServices>(
    () => ({
      userId,
      repository,
      store,
      sync,
      syncState,
      flush: async () => {
        const result = await sync.flush()
        setSyncState(result)
        return result
      },
      flushJournal,
      presence,
      setPresenceAvailable: (available) => {
        presenceBeat.setAvailable(available)
      },
      latestPosition: () => presencePosition(latestFix.fix),
      disconnectPresence: () => presenceBeat.disconnect(),
    }),
    [userId, repository, store, sync, flushJournal, syncState, presence, presenceBeat, latestFix],
  )

  useEffect(() => {
    void requestPersistentStorage()
    const flush = () => {
      void sync.flush().then(setSyncState)
      void flushJournal()
    }
    flush()
    const onVisible = () => {
      if (!document.hidden) flush()
    }
    window.addEventListener('online', flush)
    document.addEventListener('visibilitychange', onVisible)
    const timer = setInterval(flush, 30_000)
    return () => {
      window.removeEventListener('online', flush)
      document.removeEventListener('visibilitychange', onVisible)
      clearInterval(timer)
    }
  }, [sync, flushJournal])

  // Présence : battement immédiat, puis toutes les 30 s; relancé au retour au premier plan et du réseau.
  useEffect(() => {
    const unsubscribe = presenceBeat.subscribe(setPresence)
    presenceBeat.start()
    const beat = () => {
      if (!document.hidden) void presenceBeat.beat()
    }
    window.addEventListener('online', beat)
    document.addEventListener('visibilitychange', beat)
    return () => {
      unsubscribe()
      presenceBeat.stop()
      window.removeEventListener('online', beat)
      document.removeEventListener('visibilitychange', beat)
    }
  }, [presenceBeat])

  // Position jointe au battement : seulement si la localisation est déjà autorisée (jamais demandée ici).
  useEffect(() => {
    let stopWatch: (() => void) | null = null
    let cancelled = false
    const ensureWatch = () => {
      if (stopWatch || document.hidden) return
      void geolocation.permission().then((permission) => {
        if (cancelled || stopWatch || permission !== 'granted') return
        stopWatch = geolocation.watch(
          (fix) => {
            latestFix.fix = fix
          },
          () => undefined,
        )
      })
    }
    ensureWatch()
    document.addEventListener('visibilitychange', ensureWatch)
    return () => {
      cancelled = true
      document.removeEventListener('visibilitychange', ensureWatch)
      stopWatch?.()
    }
  }, [geolocation, latestFix])

  return <OperatorContext value={value}>{children}</OperatorContext>
}
